/** Reusable DNN UI component to pick a color
 * @copyright Copyright (c) .NET Foundation. All rights reserved.
 * @license MIT
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function")
        r = Reflect.decorate(decorators, target, key, desc);
    else
        for (var i = decorators.length - 1; i >= 0; i--)
            if (d = decorators[i])
                r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { h, Host } from "@stencil/core";
import { ColorInfo } from "../../utilities/colorInfo";
import { Debounce } from "../../utilities/debounce";
/** Color Picker for Dnn */
export class DnnColorPicker {
    constructor() {
        /** Sets the initial color, must be a valid 8 character hexadecimal string without the # sign. */
        this.color = "FFFFFF";
        /** Sets the width-height ratio of the color picker saturation-lightness box.
         * @example 100% renders a perfect square
         */
        this.colorBoxHeight = "50%";
        this.rgbDisplay = "flex";
        this.hslDisplay = "none";
        this.hexDisplay = "none";
        this.focused = false;
        this.handleSaturationLightnessMouseDown = (e) => {
            e.preventDefault();
            this.handleDragLightnessSaturation(e);
            window.addEventListener('mousemove', this.handleDragLightnessSaturation);
            window.addEventListener('mouseup', this.handleSaturationLightnessMouseUp);
        };
        this.handleDragLightnessSaturation = (e) => {
            const rect = this.saturationLightnessBox.getBoundingClientRect();
            let x = e.clientX - rect.left;
            if (x < 0) {
                x = 0;
            }
            if (x > rect.width) {
                x = rect.width;
            }
            x = x / rect.width;
            let y = e.clientY - rect.top;
            if (y < 0) {
                y = 0;
            }
            if (y > rect.height) {
                y = rect.height;
            }
            y = 1 - (y / rect.height);
            const newColor = new ColorInfo();
            newColor.hue = this.currentColor.hue;
            newColor.saturation = x;
            newColor.lightness = y;
            this.currentColor = newColor;
        };
        this.handleSaturationLightnessMouseUp = () => {
            window.removeEventListener('mousemove', this.handleDragLightnessSaturation);
            window.removeEventListener('mouseup', this.handleSaturationLightnessMouseUp);
        };
        this.handleHueMouseDown = (e) => {
            e.preventDefault();
            this.handleDragHue(e);
            window.addEventListener('mousemove', this.handleDragHue);
            window.addEventListener('mouseup', this.handleHueMouseUp);
        };
        this.handleHueMouseUp = () => {
            window.removeEventListener('mousemove', this.handleDragHue);
            window.removeEventListener('mouseup', this.handleHueMouseUp);
        };
        this.handleDragHue = (e) => {
            const rect = this.hueRange.getBoundingClientRect();
            let x = e.clientX - rect.left;
            if (x < 0) {
                x = 0;
            }
            if (x > rect.width) {
                x = rect.width;
            }
            x = x / rect.width * 360;
            const newColor = new ColorInfo();
            newColor.hue = x;
            newColor.saturation = this.currentColor.saturation;
            newColor.lightness = this.currentColor.lightness;
            this.currentColor = newColor;
        };
        this.handleComponentValueChange = (e, channel) => {
            let value = parseInt(e.target.value);
            if (isNaN(value)) {
                return;
            }
            const newColor = new ColorInfo();
            if (value < 0) {
                value = 0;
            }
            if (value > 255) {
                value = 255;
            }
            let r = this.currentColor.red;
            let g = this.currentColor.green;
            let b = this.currentColor.blue;
            switch (channel) {
                case 'red':
                    r = value;
                    break;
                case 'green':
                    g = value;
                    break;
                case 'blue':
                    b = value;
                    break;
                default:
                    break;
            }
            newColor.green = g;
            newColor.red = r;
            newColor.blue = b;
            this.currentColor = newColor;
        };
        this.handleHSLChange = (e, component) => {
            let value = parseInt(e.target.value);
            if (isNaN(value)) {
                return;
            }
            const newColor = new ColorInfo();
            if (value != null) {
                let h = this.currentColor.hue;
                let s = this.currentColor.saturation;
                let l = this.currentColor.lightness;
                switch (component) {
                    case "hue":
                        if (value < 0) {
                            value = 0;
                        }
                        if (value > 359) {
                            value = 0;
                        }
                        h = value;
                        break;
                    case "saturation":
                        if (value < 0) {
                            value = 0;
                        }
                        if (value > 100) {
                            value = 100;
                        }
                        s = value / 100;
                        break;
                    case "lightness":
                        if (value < 0) {
                            value = 0;
                        }
                        if (value > 100) {
                            value = 100;
                        }
                        l = value / 100;
                        break;
                    default:
                        break;
                }
                newColor.hue = h;
                newColor.saturation = s;
                newColor.lightness = l;
                this.currentColor = newColor;
            }
        };
        this.handleSaturationLightnessKeyDown = (e) => {
            let newColor = new ColorInfo();
            newColor.hue = this.currentColor.hue;
            newColor.saturation = this.currentColor.saturation;
            newColor.lightness = this.currentColor.lightness;
            let value = 0.01;
            if (e.shiftKey) {
                value = 0.1;
            }
            switch (e.key) {
                case "ArrowUp":
                    newColor.lightness += value;
                    break;
                case "ArrowDown":
                    newColor.lightness -= value;
                    break;
                case "ArrowLeft":
                    newColor.saturation -= value;
                    break;
                case "ArrowRight":
                    newColor.saturation += value;
                    break;
                default:
                    break;
            }
            this.currentColor = newColor;
        };
        this.handleHueKeyDown = (e) => {
            let newColor = new ColorInfo();
            newColor.hue = this.currentColor.hue;
            newColor.saturation = this.currentColor.saturation;
            newColor.lightness = this.currentColor.lightness;
            let value = 1;
            if (e.shiftKey) {
                value = 10;
            }
            switch (e.key) {
                case "ArrowLeft":
                    newColor.hue -= value;
                    break;
                case "ArrowRight":
                    newColor.hue += value;
                default:
                    break;
            }
            this.currentColor = newColor;
        };
    }
    colorChangedHandler(color) {
        this.colorChanged.emit(color);
    }
    handeCurrentColorChanged(newValue) {
        this.colorChangedHandler(newValue);
    }
    componentWillLoad() {
        this.handleHexChange(this.color);
    }
    componentDidLoad() {
        this.el.style.setProperty("--color-box-height", this.colorBoxHeight.toString());
    }
    getHex() {
        return this.getDoublet(this.currentColor.red) + this.getDoublet(this.currentColor.green) + this.getDoublet(this.currentColor.blue);
    }
    getContrast() {
        return this.currentColor.contrastColor;
    }
    getDoublet(value) {
        const valueString = value.toString(16).toUpperCase();
        if (valueString.length === 1) {
            return '0' + valueString;
        }
        return valueString;
    }
    handleHexChange(value) {
        const newColor = new ColorInfo();
        if (value.match(/^(?:[\da-f]{3}|[\da-f]{6})$/i).length > 0) {
            if (value.length === 3) {
                let expanded = value[0] + value[0] + value[1] + value[1] + value[2] + value[2];
                value = expanded;
            }
            newColor.red = parseInt(value.substr(0, 2), 16);
            newColor.green = parseInt(value.substr(2, 2), 16);
            newColor.blue = parseInt(value.substr(4, 2), 16);
        }
        else {
            newColor.red = this.currentColor.red;
            newColor.green = this.currentColor.green;
            newColor.blue = this.currentColor.blue;
        }
        this.currentColor = newColor;
    }
    switchColorMode(e) {
        switch (e.target.id) {
            case "rgb-switch":
                this.rgbDisplay = "none";
                this.hslDisplay = "none";
                this.hexDisplay = "flex";
                break;
            case "hex-switch":
                this.rgbDisplay = "none";
                this.hslDisplay = "flex";
                this.hexDisplay = "none";
                break;
            case "hsl-switch":
                this.rgbDisplay = "flex";
                this.hslDisplay = "none";
                this.hexDisplay = "none";
                break;
            default:
                this.rgbDisplay = "flex";
                this.hslDisplay = "none";
                this.hexDisplay = "none";
        }
    }
    render() {
        const hue = this.currentColor.hue;
        const saturation = this.currentColor.saturation;
        const lightness = this.currentColor.lightness;
        const red = this.currentColor.red;
        const green = this.currentColor.green;
        const blue = this.currentColor.blue;
        return (h(Host, { key: 'f908b04a53ce6599333a7794efb20bb2a65af251', tabIndex: this.focused ? -1 : 0, onFocus: () => this.saturationBrightnessButton.focus(), onBlur: () => this.saturationBrightnessButton.blur() }, h("div", { key: 'c4523480f16ebf5cfea121ac5d79c643a4e0c69e', class: "dnn-color-picker" }, h("div", { key: '83b8d5779d81296117903ac8e0379ab2f45c9231', class: "dnn-color-sliders" }, h("div", { key: '2de249d2eb985d1094066cf1fc263443090f01ce', class: "dnn-color-s-b", ref: (element) => this.saturationLightnessBox = element, style: { backgroundColor: `hsl(${hue},100%,50%)` }, onMouseDown: this.handleSaturationLightnessMouseDown.bind(this) }, h("button", { key: 'a80d2a3e40f688836ab882052f6ab65ac8a9b045', ref: el => this.saturationBrightnessButton = el, class: "dnn-s-b-picker", "aria-label": "Press up or down to adjust lightness, left or right to adjust saturation, hold shift to move by 10%", role: "slider", "aria-valuemin": "0", "aria-valuemax": "100", "aria-valuetext": `Saturation: ${Math.round(this.currentColor.saturation * 100)}%, Lightness: ${Math.round(this.currentColor.lightness * 100)}%`, style: {
                left: Math.round(saturation * 100) + "%",
                bottom: Math.round(lightness * 100) + "%"
            }, onKeyDown: e => this.handleSaturationLightnessKeyDown(e), onFocus: () => this.focused = true, onBlur: () => this.focused = false })), h("div", { key: 'e1e22edc8488004623ec8d87ec7258b47517037c', class: "dnn-color-bar" }, h("div", { key: '32205dfe097de2f4c71f1df2754eb9f8a2d6e7d9', class: "dnn-color-result", style: {
                backgroundColor: '#' + this.getHex(),
                boxShadow: "0 0 2px 1px " + "#" + this.getContrast()
            } }), h("div", { key: '08964c013a6769a395b5a845961f2ad1d6048dca', class: "dnn-color-hue", ref: (element) => this.hueRange = element, onMouseDown: this.handleHueMouseDown.bind(this) }, h("button", { key: 'ac7054bcb822e3517b3c44fcd2f7b9f7f2e4a72c', class: "dnn-hue-picker", "aria-label": "Press left or right to adjust hue, hold shift to move by 10 degrees", role: "slider", "aria-valuemin": "0", "aria-valuemax": "359", "aria-valuenow": Math.round(hue), style: { left: (hue / 359 * 100).toString() + "%" }, onKeyDown: (e) => this.handleHueKeyDown(e) })))), h("div", { key: '3374859dfa3b34cf237df1c814612614839aadea', class: "dnn-color-fields" }, h("div", { key: 'fe4046f3547b5251ca26a5b1cc29628c0630504f', class: "dnn-rgb-color-fields", style: { display: this.rgbDisplay } }, h("div", { key: 'ad2cf085848f5cf8552f17d37f798bae169081f8', class: "dnn-rgb-color-field" }, h("label", { key: '9e67bab0ea36a39abcf852ad3c2f527d28dc2471' }, "R"), h("input", { key: '2507e10e0fd6612b722659bc70da9b3ac6ac0276', type: "number", min: "0", max: "255", step: "1", class: "red", value: red, "aria-label": "red value", onChange: (e) => this.handleComponentValueChange(e, 'red') })), h("div", { key: '58d845aed33e870a6667fc31307f207bdf42734a', class: "dnn-rgb-color-field" }, h("label", { key: 'f36e027885207712c901a19a607ee31b57edcf45' }, "G"), h("input", { key: '03ce9f1ae4c37c311cf0b273884d7aa336914afb', type: "number", min: "0", max: "255", class: "green", value: green, "aria-label": "green value", onChange: (e) => this.handleComponentValueChange(e, 'green') })), h("div", { key: '9849f8b17444e0eb6b0f727a3f3a49ea6cbe9f98', class: "dnn-rgb-color-field" }, h("label", { key: '6434d5775c8b899e5cbdf79aaf1900e027739c16' }, "B"), h("input", { key: '0a88320ff47db01511bc7c48fd95f82d3a575947', type: "number", min: "0", max: "255", class: "blue", value: blue, "aria-label": "blue value", onChange: (e) => this.handleComponentValueChange(e, 'blue') })), h("div", { key: '2cafd9eb04193e0248c586d122693668d9a4abba', class: "dnn-color-mode-switch" }, h("button", { key: '107e1fb9b01f8f9447810de88a95f60e975bcee6', id: "rgb-switch", onClick: this.switchColorMode.bind(this), "aria-label": "switch to hexadecimal value entry" }, h("svg", { key: 'ac1611786d63808054037ef3c80578e2cdb86b96', xmlns: "http://www.w3.org/2000/svg", height: "24px", viewBox: "0 0 24 24", width: "24px", fill: "#000000" }, h("path", { key: '6889b846dad41346d0fe0f4da46c0fbd738fc1c6', d: "M0 0h24v24H0z", fill: "none" }), h("path", { key: 'afece13ea369951f847eb277d03ba63ad568d85a', d: "M7 7h10v3l4-4-4-4v3H5v6h2V7zm10 10H7v-3l-4 4 4 4v-3h12v-6h-2v4z" }))))), h("div", { key: '433bab4749873cece4786fc14ebf8dcaaa315586', class: "dnn-hsl-color-fields", style: { display: this.hslDisplay } }, h("div", { key: 'cb3443684a0630a674d2e6af6aa3719658126fe4', class: "dnn-hsl-color-field" }, h("label", { key: '6eb7a63d5a915919d45c585eca10766ac03bedce' }, "H"), h("input", { key: '8ebce2cd82de3f682f52cd9f851e37782567665d', type: "number", min: "0", max: "359", step: 1, value: Math.round(hue), "aria-label": "Hue", onChange: (e) => this.handleHSLChange(e, 'hue') })), h("div", { key: '88b1a0a344af2e4504ea86504cca3b0a0a877afb', class: "dnn-hsl-color-field" }, h("label", { key: '2b41a0003c2b7821eddac0719125e5f5df04f562' }, "S"), h("input", { key: 'd63cf42953ed94b472c7697f1c12e2274bb3da18', type: "number", min: "0", max: "100", step: 1, value: Math.round(saturation * 100), "aria-label": "Saturation", onChange: (e) => this.handleHSLChange(e, 'saturation') })), h("div", { key: '603aa4a11f53dcd81c9b1c65de6de0aafd9b5916', class: "dnn-hsl-color-field" }, h("label", { key: 'f1950202a61d91ff43c80df6241aacc1ee71c678' }, "L"), h("input", { key: '341b0079f8bf0bf8facc9d5d7be36c49972e976f', type: "number", min: "0", max: "100", step: 1, value: Math.round(lightness * 100), "aria-label": "Lightness", onChange: (e) => this.handleHSLChange(e, 'lightness') })), h("div", { key: 'bc8679b6d48eae8adabd3f27cd4fae63df20d415', class: "dnn-color-mode-switch" }, h("button", { key: 'c61834d10e93cce9d4475faf3f62fd72f977271c', id: "hsl-switch", onClick: this.switchColorMode.bind(this), "aria-label": "Switch to red, green, blue entry mode" }, h("svg", { key: '9139a6361b12820c47a2978fae13d4790667a5b9', xmlns: "http://www.w3.org/2000/svg", height: "24px", viewBox: "0 0 24 24", width: "24px", fill: "#000000" }, h("path", { key: '1bbbc318c6691269fe88f8d94182566290487434', d: "M0 0h24v24H0z", fill: "none" }), h("path", { key: '5c86619418305089e5db2035b58a686a51a0d08f', d: "M7 7h10v3l4-4-4-4v3H5v6h2V7zm10 10H7v-3l-4 4 4 4v-3h12v-6h-2v4z" }))))), h("div", { key: '0d8a617bee36c4887253f6653d0c8b986ac0b19b', class: "dnn-hex-color-fields", style: { display: this.hexDisplay } }, h("div", { key: '0f78740de447d7ffb74ea4560a21211e797bd424', class: "dnn-hex-color-field" }, h("label", { key: '7344acdb65b35cf50060b516c619ed896edd8f78' }, "HEX"), h("div", { key: '937978421c11afb45b482d39e218e5dd576f7c59', class: "hex-input" }, h("input", { key: 'd6d530cbf357c1b05c517823f7f339b0fabe6f93', type: "text", "aria-label": "Hexadecimal value", value: this.getHex(), onChange: e => this.handleHexChange(e.target.value) }), h("button", { key: '15a4c70cf71ac5030b1f955ff987d6524f523a04', class: "copy", "aria-label": "copy value" }, h("svg", { key: '49668dabf7a7a1461214b7686006c26e6aeaec91', xmlns: "http://www.w3.org/2000/svg", height: "24px", viewBox: "0 0 24 24", width: "24px", fill: "#000000" }, h("path", { key: 'fcacb45fb8db45f3cd7d4c0a6d6014780188a82a', d: "M0 0h24v24H0z", fill: "none" }), h("path", { key: '41569930b5b3ae6d43de0438dcb15e24378bbed1', d: "M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" }))))), h("div", { key: 'b5d101d894b54e4f71c3d8790647cd88456edfac', class: "dnn-color-mode-switch" }, h("button", { key: '99fa128f296cd477f4c746ccfb2720c7852a8a28', id: "hex-switch", onClick: this.switchColorMode.bind(this), "aria-label": "Switch to hue saturation lightness values" }, h("svg", { key: '80a1c8189a305aed9142a435967390db1a147f09', xmlns: "http://www.w3.org/2000/svg", height: "24px", viewBox: "0 0 24 24", width: "24px", fill: "#000000" }, h("path", { key: '00a595f9a95d749c6cf5c55d0906abbd016f3154', d: "M0 0h24v24H0z", fill: "none" }), h("path", { key: '51ef41e5b8beee6a566f145443c9f9fa8c5c2984', d: "M7 7h10v3l4-4-4-4v3H5v6h2V7zm10 10H7v-3l-4 4 4 4v-3h12v-6h-2v4z" })))))))));
    }
    static get is() { return "dnn-color-picker"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["dnn-color-picker.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["dnn-color-picker.css"]
        };
    }
    static get properties() {
        return {
            "color": {
                "type": "string",
                "attribute": "color",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Sets the initial color, must be a valid 8 character hexadecimal string without the # sign."
                },
                "getter": false,
                "setter": false,
                "reflect": false,
                "defaultValue": "\"FFFFFF\""
            },
            "colorBoxHeight": {
                "type": "string",
                "attribute": "color-box-height",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [{
                            "name": "example",
                            "text": "100% renders a perfect square"
                        }],
                    "text": "Sets the width-height ratio of the color picker saturation-lightness box."
                },
                "getter": false,
                "setter": false,
                "reflect": false,
                "defaultValue": "\"50%\""
            }
        };
    }
    static get states() {
        return {
            "currentColor": {},
            "rgbDisplay": {},
            "hslDisplay": {},
            "hexDisplay": {},
            "focused": {}
        };
    }
    static get events() {
        return [{
                "method": "colorChanged",
                "name": "colorChanged",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [{
                            "name": "see",
                            "text": ".. /../utilities/colorInfo.ts"
                        }],
                    "text": "Fires up when the color is changed and emits a ColorInfo object"
                },
                "complexType": {
                    "original": "ColorInfo",
                    "resolved": "ColorInfo",
                    "references": {
                        "ColorInfo": {
                            "location": "import",
                            "path": "../../utilities/colorInfo",
                            "id": "src/utilities/colorInfo.ts::ColorInfo"
                        }
                    }
                }
            }];
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "currentColor",
                "methodName": "handeCurrentColorChanged"
            }];
    }
}
__decorate([
    Debounce(100)
], DnnColorPicker.prototype, "colorChangedHandler", null);
//# sourceMappingURL=dnn-color-picker.js.map
